// React i hooks
import { memo, useCallback, useEffect, useRef, useState } from "react";

// Helpers
import { useTranslate } from "helpers";
import useUrlFilters from "services/hooks/useUrlFilters";

// API i konstante
import { FleetVehicleSearchFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleSearchForm } from "services/api/fleet/vehicles/consts";
import { AdvancedSearchFormValues, buildFilterChips } from "services/search";
import { getFleetSettingsAPI } from "services/api/settings/fleet/api";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// Dropdown komponente
import VehicleCategoryDropdown from "ui/components/Dropdown/VehicleCategoryDropdown/VehicleCategoryDropdown";
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";

// Tipovi
import { VehicleSearchFilterProps } from "./types";
import VehicleSearchFiltersModal from "./VehicleSearchFiltersModal";
import { FUEL_LABELS } from "./consts";
import { Chip } from "ui/components/Chip";
import { TableViewToggle } from "ui/components/Table/TableViewToggle";
import VehicleStatusDropdown from "ui/components/Dropdown/VehicleStatusDropdown/VehicleStatusDropdown";

// Chip-config za URL-sinkronizirane filtere (label + prikaz vrijednosti). Novi filter = novi redak.
const URL_FILTER_CHIPS = {
  condition: { label: "Stanje", display: (v: string) => (v === "new" ? "Novo" : "Rabljeno") },
  fuelType: { label: "Gorivo", display: (v: string) => FUEL_LABELS[v] ?? v },
};

/**
 * Upsertira chip za jedan filter unutar advancedSearch liste.
 * Ako chip s istim name-om postoji → zamijeni ga; ako ne → dodaj.
 */
const upsertAdvancedSearch = (
  list: AdvancedSearchFormValues[],
  chip: AdvancedSearchFormValues
): AdvancedSearchFormValues[] => {
  const exists = list.some((c) => c.name === chip.name);
  if (exists) return list.map((c) => (c.name === chip.name ? { ...c, ...chip } : c));
  return [...list, chip];
};

/**
 * Kaskadna polja koja se resetiraju kad se promijeni parent filter.
 * Ključ je parent field, value je lista dječjih fieldova koje treba nullati.
 */
const CASCADE_CHILDREN: Record<string, string[]> = {
  vehicleCategoryId: ["vehicleManufacturerId", "vehicleModelId", "vehicleTypeId"],
  vehicleManufacturerId: ["vehicleModelId", "vehicleTypeId"],
  vehicleModelId: ["vehicleTypeId"],
};

const VehicleSearchFilters: React.FC<VehicleSearchFilterProps> = (props) => {
  const { onSearchChange, viewType, onViewTypeChange } = props;

  const { t } = useTranslate();

  // URL-sinkronizirani filteri (drill-down s dashboarda + share/reload). Kaskadni odabir
  // (marka -> model -> tip) automatski upisuje/mičе paramove jer handleri zovu setFormValues.
  const [formValues, setFormValues] = useUrlFilters<FleetVehicleSearchFormValues>(
    initialFleetVehicleSearchForm,
    {
      condition: { param: "condition" },
      fuelType: { param: "fuel" },
      vehicleManufacturerId: { param: "manufacturer" },
      vehicleModelId: { param: "model" },
      vehicleTypeId: { param: "type" },
      statuses: { param: "status", type: "list" },
      officeId: { param: "office" },
    }
  );
  const [showModal, setShowModal] = useState<boolean>(false);
  const [hasMultipleCategories, setHasMultipleCategories] = useState<boolean>(false);

  // Ref držimo da handleri mogu biti useCallback([]) — parent ne memoizira onSearchChange pa bi inače svaki
  // render rušio memoization djece (dropdown-i i chip-ovi su React.memo).
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });

  // Mount: dohvat tenant fleet settings; ako tenant ima točno jednu kategoriju vozila — auto-popuni
  // vehicleCategoryId. Bilo koji ishod (auto-fill, više kategorija, error) pokreće PRVI search poziv.
  useEffect(() => {
    const fetchCategoriesAndInitialSearch = async () => {
      // Kreni od trenutnih vrijednosti — useUrlFilters je već popunio URL-sinkronizirana polja (condition).
      let categoryPatch: Partial<FleetVehicleSearchFormValues> = {};
      try {
        const result = await getFleetSettingsAPI();
        const selected = result.selectedVehicleCategories;
        setHasMultipleCategories(selected.length > 1);
        if (selected.length === 1) {
          categoryPatch = { vehicleCategoryId: selected[0].id };
        }
      } catch {
        // Padanje kategorija ne smije blokirati prikaz vozila — pretraži s default formom.
      }

      // Deklarativno dodaj chipove za URL-sinkronizirane filtere koji imaju vrijednost (skalira na više filtera).
      const base = { ...formValues, ...categoryPatch };
      const initial: FleetVehicleSearchFormValues = {
        ...base,
        advancedSearch: buildFilterChips(base, URL_FILTER_CHIPS).reduce(
          upsertAdvancedSearch,
          base.advancedSearch
        ),
      };

      setFormValues(initial);
      onSearchChangeRef.current(initial);
    };
    fetchCategoriesAndInitialSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jedini handler za sva 4 dropdown-a: radi update state-a (uključujući kaskadni reset djece)
  // i istim pozivom okida onSearchChange s finalnim vrijednostima — bez useEffect chain-a.
  const handleDropdownChange = useCallback(
    (e: any, name: string, item: DropdownOption) => {
      setFormValues((prev) => {
        const chip: AdvancedSearchFormValues = {
          value: item.value,
          name,
          label: e.target.dataset?.label,
          text: item.text,
          displayName: item.displayName,
        };

        const cascadeChildren = CASCADE_CHILDREN[name] ?? [];
        const filteredChips = prev.advancedSearch.filter(
          (c) => !cascadeChildren.includes(c.name)
        );
        const next: FleetVehicleSearchFormValues = {
          ...prev,
          [name]: item.value,
          advancedSearch: upsertAdvancedSearch(filteredChips, chip),
        };
        cascadeChildren.forEach((field) => {
          (next as any)[field] = field === "vehicleManufacturerId" ? "" : null;
        });

        onSearchChangeRef.current(next);
        return next;
      });
    },
    []
  );

  // Filtriranje po statusu vozila. Nema chip u sažetku jer je odabir već vidljiv
  // na samom dropdownu.
  const handleStatusesChange = useCallback((_name: string, statuses: string[]) => {
    setFormValues((prev) => {
      const next: FleetVehicleSearchFormValues = { ...prev, statuses };
      onSearchChangeRef.current(next);
      return next;
    });
  }, []);

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialFleetVehicleSearchForm);
    onSearchChangeRef.current(initialFleetVehicleSearchForm);
  }, []);

  const handleAdditionalFilterClick = useCallback(
    (modalFormValues: FleetVehicleSearchFormValues) => {
      setFormValues(modalFormValues);
      onSearchChangeRef.current(modalFormValues);
      setShowModal(false);
    },
    []
  );

  const handleRemoveFilterClick = useCallback(
    (_id: string, name: string, _text: string) => {
      setFormValues((prev) => {
        // Kaskadno brisanje: ako se uklanja parent, ukloni i svu njegovu djecu.
        const fieldsToRemove = [name, ...(CASCADE_CHILDREN[name] ?? [])];
        const next: FleetVehicleSearchFormValues = {
          ...prev,
          advancedSearch: prev.advancedSearch.filter(
            (c) => !fieldsToRemove.includes(c.name)
          ),
        };
        fieldsToRemove.forEach((field) => {
          (next as any)[field] = null;
        });
        onSearchChangeRef.current(next);
        return next;
      });
    },
    []
  );

  return (
    <>
      <div className="overflow-x-scroll md:overflow-x-visible scrollbar-hide px-4 xl:px-0">
        <div className="flex justify-between items-center mb-3">
          <div className="flex flex-wrap gap-3">
            <div className="hidden gap-3 xl:flex">
              {hasMultipleCategories && (
                <div className="min-w-[260px]">
                  <VehicleCategoryDropdown
                    placeholder={t("admin.vehicle.category.placeholder")}
                    name="vehicleCategoryId"
                    value={formValues.vehicleCategoryId}
                    onChange={handleDropdownChange}
                    width="full"
                  />
                </div>
              )}
              <div className="min-w-[260px]">
                <VehicleManufacturerDropdown
                  placeholder={t("admin.vehicle.manufacturer.placeholder")}
                  name="vehicleManufacturerId"
                  vehicleCategoryId={formValues.vehicleCategoryId}
                  value={formValues.vehicleManufacturerId}
                  onChange={handleDropdownChange}
                  width="full"
                />
              </div>
              <div className="min-w-[260px]">
                <VehicleModelDropdown
                  placeholder={t("admin.vehicle.model.placeholder")}
                  name="vehicleModelId"
                  vehicleManufacturerId={formValues.vehicleManufacturerId}
                  value={formValues.vehicleModelId}
                  onChange={handleDropdownChange}
                  width="full"
                />
              </div>
              <div className="min-w-[260px]">
                <VehicleTypeDropdown
                  placeholder={t("admin.vehicle.type.placeholder")}
                  name="vehicleTypeId"
                  vehicleManufacturerId={formValues.vehicleManufacturerId}
                  vehicleModelId={formValues.vehicleModelId}
                  value={formValues.vehicleTypeId}
                  onChange={handleDropdownChange}
                />
              </div>
              {/* Stanje, gorivo i poslovnica su u dodatnoj pretrazi — traka drži samo
                  kaskadu vozila i status, da ostane čitljiva. */}
              <div className="min-w-[180px]">
                <VehicleStatusDropdown
                  name="statuses"
                  placeholder="Status vozila"
                  value={formValues.statuses}
                  onChange={handleStatusesChange}
                />
              </div>
            </div>
            <Button
              variant="outlined"
              startIcon="/media/icons/text-search.svg"
              onClick={() => setShowModal(true)}
            />
            <div className="mr-3 md:mr-6 lg:md-12 flex items-center">
              <span
                onClick={handleResetFormClick}
                className="text-xs font-bold cursor-pointer text-gray-700"
              >
                {t("admin.reset")}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="flex items-center justify-between ">
              <div className="flex items-center">
                <div className="hidden items-center gap-4 text-xs text-slate-500 mr-6 md:flex">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                    {t("admin.vehicle.new")}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    {t("admin.vehicle.old")}
                  </span>
                </div>
              </div>
            </div>
            <TableViewToggle
              value={viewType}
              onChange={onViewTypeChange}
              options={[
                { value: "table", label: t("admin.table.viewtoggle.table"), icon: "/media/icons/table.svg" },
                { value: "grid", label: t("admin.table.viewtoggle.grid"), icon: "/media/icons/grid.svg" },
              ]}
            />
          </div>
        </div>
        <div className="mb-3 flex gap-1">
          {formValues.advancedSearch &&
            formValues.advancedSearch.map((item, index: number) => {
              return (
                <Chip
                  name={item.name}
                  id={item.value}
                  text={`${item.label ? `${item.label}: ` : ""}${item.displayName ?? (typeof item.text === "string" ? item.text : "")}`}
                  key={index}
                  onClick={handleRemoveFilterClick}
                />
              );
            })}
        </div>
      </div>
      <VehicleSearchFiltersModal
        show={showModal}
        onSuccess={handleAdditionalFilterClick}
        onClose={() => setShowModal(false)}
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </>
  );
};
export default memo(VehicleSearchFilters);
